import React, { useState } from 'react';
import styles from './flyerList.module.scss';
import { filterFlyers, FlyerFilter, sortFlyers } from '../../helpers/flyers';
import { getCityNameFromLocation } from '../../helpers/location';
import useTranslation from 'next-translate/useTranslation';
import cx from 'classnames';
import { Flyer } from '../../definitions/FlyerDefinitions';
import FlyerThumbnail from '../flyerThumbnail';
import useBlackFriday from '../../hooks/useBlackFriday';

interface FlyerListProps {
  flyers: Flyer[];
  location: string;
  category: string;
  shouldRenderTopBar?: boolean;
  postalCode?: string;
}

export default function FlyerList({
  flyers,
  location,
  category,
  postalCode,
  shouldRenderTopBar,
}: FlyerListProps): JSX.Element {
  const { t } = useTranslation('application');
  const { isBlackFridayPage } = useBlackFriday({});
  const [filter, setFilter] = useState<FlyerFilter>('featured');
  const { premiumFlyers, normalFlyers } = filterFlyers(sortFlyers(flyers), category, filter);
  const isEmpty = Boolean(premiumFlyers.length + normalFlyers.length === 0);

  function getH1() {
    let title = 'flyers_for_postal_code_category';
    if (location) {
      title = 'flyers_for_city_postal_code_category';
      if (category !== 'All Flyers') {
        title = 'category_flyers_for_city_postal_code_category';
      } else {
        title = 'index_flyers_h1';
      }
    }

    const city = getCityNameFromLocation(location);

    return t(title, {
      postalCode,
      city: category !== 'Black Friday' ? city : '',
      category: t(category),
    });
  }

  function renderTopBar() {
    return (
      <div className={styles.topbar}>
        <div className={styles.sorTabs}>
          <button
            onClick={() => {
              setFilter('featured');
            }}
            className={cx(filter === 'featured' && styles.selected)}
            title={t('featured_sort_help')}
          >
            {t('featured')}
          </button>
          <button
            title={t('latest_sort_help')}
            className={cx(filter === 'latest' && styles.selected)}
            onClick={() => {
              setFilter('latest');
            }}
          >
            {t('latest')}
          </button>
          <button
            title={t('alphabetical_sort_help')}
            className={cx(filter === 'alphabetical' && styles.selected)}
            onClick={() => {
              setFilter('alphabetical');
            }}
          >
            {t('alphabetical')}
          </button>
        </div>

        {postalCode && <h1 className={styles.title}>{getH1()}</h1>}
      </div>
    );
  }

  return (
    <>
      {shouldRenderTopBar && renderTopBar()}
      <div className={styles.content}>
        {premiumFlyers.length > 0 &&
          premiumFlyers.map((flyer: Flyer) => {
            return (
              <span key={`${flyer.id}-${filter}`} className={styles.premiumFlyerWrapper}>
                <FlyerThumbnail flyer={flyer} location={location} forceNonePremium={false} />
              </span>
            );
          })}
        {normalFlyers.length > 0 &&
          normalFlyers.map((flyer: Flyer) => {
            return (
              <span key={flyer.id}>
                <FlyerThumbnail
                  flyer={flyer}
                  location={location}
                  forceNonePremium={filter === 'latest' || filter === 'alphabetical'}
                />
              </span>
            );
          })}
      </div>
      <div className={styles.empty}>
        {isEmpty && !isBlackFridayPage && t('flyers_empty', { postalCode })}
        {isEmpty && isBlackFridayPage && t('flyers_empty_blackFriday')}
      </div>
    </>
  );
}
